import _ from 'underscore';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';

type NavigationViewType = {
  name: string,
  params: any,
};

type NavigationContextType = {
  getTopView: () => NavigationViewType | undefined,
  getViewParams: (viewName: string) => any,
  setViewParams: (viewName: string, params: any) => void,
  pushLocalView: (viewName: string, params: any) => void,
  popLocalView: () => NavigationViewType | undefined,
  // replaceLocalView: (viewName: string, params: any) => void,
  updated: any,
};

const LocalNavigationContext = React.createContext<NavigationContextType | null>(null);

export const LocalNavigationProvider = ({ children }) => {
  // use a ref to store the vew stack to eunsure for all context value instances (even
  // for the ones captured in closures) can access the latest view stack
  // and to trigger re-render when the view stack is updated, we use a state to store a dummy object
  const localViewStackRef = useRef<Array<NavigationViewType>>([]);
  const activeElementRef = useRef<Element | null>();
  const [updated, setUpdated] = useState({});
  // const [localViewStack, setLocalViewStack] = useState<Array<NavigationViewType>>([]);

  const getTopView = useCallback(() => _.last(localViewStackRef.current), []);

  const pushLocalView = useCallback((viewName, params) => {
    const { name } = _.last(localViewStackRef.current) || {};
    if (name !== viewName) {
      activeElementRef.current = document.activeElement;
      localViewStackRef.current.push({ name: viewName, params });
    } else {
      localViewStackRef.current = localViewStackRef.current.slice(0, -1).concat({ name: viewName, params });
    }
    setUpdated({}); // force re-render
  }, []);

  const popLocalView = useCallback(() => {
    if (activeElementRef.current && activeElementRef.current instanceof HTMLElement) {
      activeElementRef.current.focus();
    }
    const topView = _.last(localViewStackRef.current);
    localViewStackRef.current = localViewStackRef.current.slice(0, -1);
    setUpdated({}); // force re-render
    return topView;
  }, []);

  // const replaceLocalView = useCallback((viewName, params) => {
  // }, []);

  const getViewParams = useCallback((viewName) => {
    const viewIndex = _.findLastIndex(localViewStackRef.current, { name: viewName });
    if (viewIndex >= 0) {
      return localViewStackRef.current[viewIndex].params || {};
    }
    return null;
  }, []);

  const setViewParams = useCallback((viewName, params) => {
    const viewIndex = _.findLastIndex(localViewStackRef.current, { name: viewName });
    if (viewIndex >= 0) {
      localViewStackRef.current[viewIndex].params = params;
      setUpdated({}); // force re-render
    }
  }, []);

  const value: NavigationContextType = {
    getTopView,
    getViewParams,
    setViewParams,
    pushLocalView,
    popLocalView,
    // replaceLocalView,
    updated,
  };

  return (
    <LocalNavigationContext.Provider value={value}>
      {children}
    </LocalNavigationContext.Provider>
  );
};

export const useLocalNavigation = () => {
  const navigation = useContext(LocalNavigationContext);
  return navigation;
};
/* istanbul ignore next */
// has not been used in ads studio so skipped
// TODO: add test for withLocalNavigation
export const withLocalNavigation = (Component, viewName) => {
  const HOC = (props) => {
    const { getTopView, getViewParams, updated } = useContext(LocalNavigationContext) || {};
    const topView = getTopView && getTopView();
    const params = getViewParams && getViewParams(viewName);
    return (
      <Component
        {...props}
        open={!!params} // the view is in stack
        isTopView={topView && topView.name === viewName}
        localRouteUpdated={updated} // force re-render
        {...params}
      />
    );
  };
  return HOC;
};

export const LocalNavigationRoute = ({ viewName, render, ...rest }) => {
  const { getTopView, getViewParams, updated } = useContext(LocalNavigationContext) || {};
  const topView = getTopView && getTopView();
  const params = getViewParams && getViewParams(viewName);
  const Comp = useMemo(() => {
    const ret = render({
      open: !!params,
      isTopView: topView && topView.name === viewName,
      localRouteUpdated: updated, // force re-render
      ...params,
      ...rest,
    });
    return ret;
  }, [render, params, topView, viewName, updated, rest]);
  return Comp;
};
